import { useState, useEffect } from "react";
import { Heart, Download, ChevronLeft, ChevronRight, X, Pencil, StickyNote, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, downloadUrl, apiError } from "../lib/api";
import { accentFor } from "../lib/accents";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const FIELD_ROWS = [
  { key: "hook", label: "Hook" },
  { key: "angle", label: "Angle" },
  { key: "offer", label: "Offer" },
];

const relativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

export const AdDetailModal = ({ ad, categories, onClose, onFavoriteChange, onNavigate, hasPrev, hasNext, isAdmin = false, onEdit, onCategoryClick, onNoteCountChange }) => {
  const [fav, setFav] = useState(ad?.is_favorite || false);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  useEffect(() => {
    setFav(ad?.is_favorite || false);
    if (ad?.id) api.post(`/ads/${ad.id}/view`).catch(() => {});
  }, [ad?.id, ad?.is_favorite]);

  useEffect(() => {
    if (!ad?.id) return;
    setNotesLoading(true);
    setNewNote("");
    setEditingId(null);
    api.get(`/ads/${ad.id}/notes`)
      .then((r) => setNotes(r.data))
      .catch(() => setNotes([]))
      .finally(() => setNotesLoading(false));
  }, [ad?.id]);

  useEffect(() => {
    if (!ad) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && hasPrev) onNavigate?.(-1);
      else if (e.key === "ArrowRight" && hasNext) onNavigate?.(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ad, hasPrev, hasNext, onNavigate]);

  if (!ad) return null;
  const cats = (ad.category_ids || []).map((id) => categories.find((c) => c.id === id)).filter(Boolean);

  const toggleFav = async () => {
    try {
      const { data } = await api.post(`/favorites/${ad.id}`);
      setFav(data.is_favorite);
      onFavoriteChange?.(ad.id, data.is_favorite);
      toast.success(data.is_favorite ? "Saved to Favorites" : "Removed from Favorites");
    } catch (e) {
      toast.error("Could not update favorite");
    }
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/ads/${ad.id}/notes`, { text });
      setNotes((prev) => [data, ...prev]);
      setNewNote("");
      onNoteCountChange?.(ad.id, notes.length + 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditingText(n.text); };
  const cancelEdit = () => { setEditingId(null); setEditingText(""); };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text) return cancelEdit();
    try {
      const { data } = await api.put(`/notes/${editingId}`, { text });
      setNotes((prev) => prev.map((n) => (n.id === editingId ? { ...n, ...data } : n)));
      cancelEdit();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      const remaining = notes.filter((n) => n.id !== noteId);
      setNotes(remaining);
      onNoteCountChange?.(ad.id, remaining.length);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = downloadUrl(ad.id, "original");
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Downloading...");
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent hideCloseButton className="max-w-4xl p-0 gap-0 rounded-2xl overflow-hidden max-h-[95vh] flex flex-col" data-testid="ad-detail-modal">
        <button
          data-testid="ad-detail-close-btn"
          onClick={() => onClose()}
          aria-label="Close"
          className="absolute right-3 top-3 z-40 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-lg flex items-center justify-center hover:bg-[#DF1283] hover:text-white transition-colors"
        >
          <X size={20} className="text-[#000000]" />
        </button>
        {hasPrev && (
          <button
            data-testid="ad-detail-prev-btn"
            onClick={() => onNavigate?.(-1)}
            aria-label="Previous ad"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-lg flex items-center justify-center hover:bg-[#0FFFE2] transition-colors"
          >
            <ChevronLeft size={20} className="text-[#000000]" />
          </button>
        )}
        {hasNext && (
          <button
            data-testid="ad-detail-next-btn"
            onClick={() => onNavigate?.(1)}
            aria-label="Next ad"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/95 backdrop-blur border border-gray-200 shadow-lg flex items-center justify-center hover:bg-[#0FFFE2] transition-colors"
          >
            <ChevronRight size={20} className="text-[#000000]" />
          </button>
        )}
        <div className="overflow-y-auto flex-1 grid md:grid-cols-2" data-testid="ad-detail-scroll">
          <div className="bg-gray-50 flex items-center justify-center min-h-[320px] max-h-[80vh] overflow-hidden">
            {ad.media_type === "video" ? (
              <video
                data-testid="ad-detail-video"
                controls
                className="max-h-[80vh] w-full"
                src={mediaUrl(ad.id, ad.variants?.includes("feed") ? "feed" : "original")}
                poster={ad.has_thumbnail ? mediaUrl(ad.id, "thumb") : undefined}
              />
            ) : (
              <img data-testid="ad-detail-image" src={mediaUrl(ad.id, "original")} alt={ad.title} className="max-h-[80vh] w-full object-contain" />
            )}
          </div>
          <div className="p-8 flex flex-col">
            <DialogTitle asChild>
              <h2 className="font-display font-black text-2xl text-[#000000] leading-tight pr-8" data-testid="ad-detail-title">{ad.title}</h2>
            </DialogTitle>
            <DialogDescription className="sr-only">Ad detail view with download and favorite options</DialogDescription>
            {cats.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {cats.map((c) => {
                  const a = accentFor(c.color);
                  return (
                    <button
                      key={c.id}
                      data-testid={`ad-detail-category-${c.slug}`}
                      onClick={() => onCategoryClick?.(c.id)}
                      title={`See all ads in ${c.name}`}
                      className="px-3 py-1 rounded-full text-xs font-bold hover:scale-105 transition-transform cursor-pointer"
                      style={{ background: a.hex, color: a.text }}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            )}
            {ad.description && <p className="text-sm text-gray-600 mt-4 leading-relaxed">{ad.description}</p>}
            {FIELD_ROWS.some((f) => ad[f.key]) && (
              <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                {FIELD_ROWS.map((f) => ad[f.key] && (
                  <div key={f.key} className="flex gap-3 text-sm" data-testid={`ad-detail-${f.key}`}>
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400 w-16 pt-0.5 shrink-0">{f.label}</span>
                    <span className="text-[#000000] leading-relaxed">{ad[f.key]}</span>
                  </div>
                ))}
              </div>
            )}
            {ad.transcript && (
              <details className="mt-4 border-t border-gray-100 pt-4 group" data-testid="ad-detail-transcript">
                <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-[0.1em] text-gray-400 hover:text-[#000000] flex items-center gap-1.5 list-none">
                  <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                  Voiceover transcript
                </summary>
                <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap mt-2 pl-4 border-l-2 border-[#0FFFE2]">{ad.transcript}</p>
                <button
                  data-testid="download-transcript-btn"
                  onClick={() => {
                    const safe = (ad.title || "transcript").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
                    const blob = new Blob([ad.transcript], { type: "text/plain;charset=utf-8" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = `${safe}-transcript.txt`;
                    document.body.appendChild(a); a.click();
                    document.body.removeChild(a); URL.revokeObjectURL(url);
                  }}
                  className="mt-3 ml-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#000000] bg-[#0FFFE2] hover:bg-[#0FFFE2] rounded-full px-3 py-1.5 transition-colors"
                >
                  <Download size={11} /> Download transcript
                </button>
              </details>
            )}
            <div className="mt-auto pt-6 space-y-3">
              <div className="border-t border-gray-100 pt-4" data-testid="ad-detail-notes">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500 flex items-center gap-1.5">
                    <StickyNote size={12} className="text-[#FC861E]" /> Your creative notes ({notes.length})
                  </label>
                </div>
                {notesLoading ? (
                  <div className="text-xs text-gray-400 py-2">Loading your notes…</div>
                ) : (
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="notes-list">
                    {notes.map((n) => (
                      <div key={n.id} className="rounded-lg bg-[#FFF8F0] border border-[#FC861E]/40 border-l-4 border-l-[#FC861E] p-2.5" data-testid={`note-item-${n.id}`}>
                        {editingId === n.id ? (
                          <div>
                            <textarea
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              rows={3}
                              className="w-full rounded-md border border-gray-300 bg-white text-[#000000] px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E]"
                              autoFocus
                            />
                            <div className="flex gap-2 mt-1.5 justify-end">
                              <button onClick={cancelEdit} className="text-[10px] font-bold uppercase text-gray-500 hover:text-[#000000] px-2 py-1">Cancel</button>
                              <button data-testid={`note-save-btn-${n.id}`} onClick={saveEdit} className="text-[10px] font-bold uppercase text-white bg-[#000000] rounded-full px-3 py-1 hover:bg-[#4A0A66]">Save</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-[#000000] whitespace-pre-wrap leading-relaxed">{n.text}</p>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-gray-500 uppercase tracking-[0.06em]">{relativeTime(n.updated_at || n.created_at)}</span>
                              <div className="flex gap-1">
                                <button data-testid={`note-edit-btn-${n.id}`} onClick={() => startEdit(n)} className="p-1 rounded-full text-gray-500 hover:text-[#000000] hover:bg-white" title="Edit note"><Pencil size={11} /></button>
                                <button data-testid={`note-delete-btn-${n.id}`} onClick={() => removeNote(n.id)} className="p-1 rounded-full text-gray-500 hover:text-[#DF1283] hover:bg-white" title="Delete note"><Trash2 size={11} /></button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {notes.length === 0 && (
                      <div className="text-xs text-gray-400 py-1">No notes yet — jot your first one below.</div>
                    )}
                  </div>
                )}
                <div className="mt-3 rounded-xl border-2 border-[#FC861E] bg-[#FFF8F0] p-3">
                  <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-[#FC861E] mb-2">
                    <Plus size={11} strokeWidth={3} /> Add a new note
                  </label>
                  <textarea
                    data-testid="ad-detail-new-note-input"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                    maxLength={4000}
                    placeholder="Jot down what inspires you. Let your creativity flow — capture the ideas this creative sparks…"
                    className="w-full rounded-lg border border-gray-300 bg-white text-[#000000] placeholder:text-gray-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FC861E] focus:border-[#FC861E] transition-colors resize-y min-h-[70px]"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-500">Notes stay saved even if you unfavorite the ad. {newNote.length > 0 && <span className="text-gray-400">· {newNote.length}/4000</span>}</span>
                    <button
                      data-testid="ad-detail-add-note-btn"
                      onClick={addNote}
                      disabled={!newNote.trim() || saving}
                      className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.08em] text-white bg-[#FC861E] rounded-full px-4 py-1.5 hover:bg-[#000000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus size={11} /> {saving ? "Adding…" : "Add note"}
                    </button>
                  </div>
                </div>
              </div>
              <button
                data-testid="ad-detail-favorite-btn"
                onClick={toggleFav}
                className={`w-full flex items-center justify-center gap-2 rounded-full px-6 py-2.5 font-bold text-sm uppercase tracking-[0.1em] transition-colors duration-200 border ${
                  fav ? "bg-[#DF1283] text-white border-[#DF1283]" : "bg-white text-[#000000] border-gray-300 hover:border-[#DF1283] hover:text-[#DF1283]"
                }`}
              >
                <Heart size={16} fill={fav ? "white" : "none"} />
                {fav ? "Saved to Favorites" : "Save to Favorites"}
              </button>
              {isAdmin && (
                <button
                  data-testid="ad-detail-admin-edit-btn"
                  onClick={() => onEdit?.(ad)}
                  className="w-full flex items-center justify-center gap-2 bg-[#9E1B32] text-white rounded-full px-6 py-2.5 font-bold text-sm uppercase tracking-[0.1em] hover:bg-[#7B1226] transition-colors duration-200"
                  title="Edit this ad (admin only)"
                >
                  <Pencil size={16} />
                  Edit Ad
                </button>
              )}
              <button
                data-testid="ad-detail-download-btn"
                onClick={download}
                className="w-full flex items-center justify-center gap-2 bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm uppercase tracking-[0.1em] hover:bg-[#4A0A66] transition-colors duration-200"
              >
                <Download size={16} />
                Download
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
